<template>
    <div class="report-builder">
        <!-- 보고서 목록 -->
        <div class="report-list-section">
            <!-- 로딩 -->
            <div v-if="loading" class="state-container">
                <i class="fas fa-spinner fa-spin state-icon"></i>
                <p class="state-text">보고서 목록을 불러오는 중...</p>
            </div>

            <!-- 빈 상태 -->
            <div v-else-if="filteredReports.length === 0" class="state-container">
                <i class="fas fa-file-times state-icon empty-icon"></i>
                <p class="state-text">
                    {{ reports.length === 0 ? "생성된 보고서가 없습니다" : "필터 조건에 맞는 보고서가 없습니다" }}
                </p>
                <p class="state-sub">
                    {{
                        reports.length === 0
                            ? "워크플로우 산정 완료 후 보고서를 생성하세요"
                            : "다른 필터를 선택해 보세요"
                    }}
                </p>
                <button v-if="reports.length === 0" class="action-btn btn-create mt-3" @click="openCreateModal">
                    <i class="fas fa-plus"></i>
                    첫 보고서 생성
                </button>
            </div>

            <!-- 보고서 카드 그리드 -->
            <div v-else class="report-grid">
                <div
                    v-for="report in filteredReports"
                    :key="report.reportIdx"
                    class="report-card"
                    :class="{
                        'card-published': report.reportStat === 'PUBLISHED',
                        'card-draft': report.reportStat === 'DRAFT',
                    }"
                >
                    <!-- 썸네일 영역 (이미지 Blob → Base64 처리로 CSP 대응) -->
                    <div class="card-thumbnail" @click="openPreviewModal(report)">
                        <img
                            v-if="report.thumbnailDataUrl"
                            :src="report.thumbnailDataUrl"
                            :alt="report.title"
                            class="thumbnail-img"
                            @error="handleThumbnailError($event, report)"
                        />
                        <div v-else-if="report.thumbnailLoading" class="thumbnail-placeholder thumbnail-loading">
                            <i class="fas fa-spinner fa-spin placeholder-icon"></i>
                        </div>
                        <div v-else class="thumbnail-placeholder">
                            <i class="fas fa-file-pdf placeholder-icon"></i>
                        </div>
                        <div class="thumbnail-overlay" v-if="report.previewUrl">
                            <i class="fas fa-search-plus"></i>
                            미리보기
                        </div>
                    </div>

                    <!-- 카드 본문 -->
                    <div class="card-body">
                        <!-- 제목 + 드롭다운 -->
                        <div class="card-title-row">
                            <h4 class="card-title" :title="report.title">{{ report.title || "제목 없음" }}</h4>

                            <CustomDropdown :items="getReportMenuItems(report)" align="right">
                                <template #trigger>
                                    <span class="report-card-trigger" title="메뉴">
                                        <i class="fas fa-ellipsis-v report-card-trigger-icon"></i>
                                    </span>
                                </template>
                            </CustomDropdown>
                        </div>
                        <!-- 상태 + 옵션 배지 -->
                        <div class="card-badges">
                            <span class="badge-stat" :class="getStatClass(report.reportStat)">
                                {{ getStatText(report.reportStat) }}
                            </span>
                            <span
                                v-if="report.reportOptions && report.reportOptions.includeEvidence"
                                class="badge-option"
                            >
                                증빙
                            </span>
                            <span
                                v-if="report.reportOptions && report.reportOptions.includeTargetStatus"
                                class="badge-option"
                            >
                                목표
                            </span>
                            <span
                                v-if="report.reportOptions && report.reportOptions.includeAuditHistory"
                                class="badge-option"
                            >
                                이력
                            </span>
                            <span v-if="report.evidenceCount > 0" class="badge-evidence">
                                <i class="fas fa-file-alt"></i> 증빙 {{ report.evidenceCount }}건
                            </span>
                        </div>
                        <!-- 메타 정보 -->
                        <div class="card-meta">
                            <div class="meta-line">
                                <i class="fas fa-calendar-alt meta-icon"></i>
                                대상기간 : {{ formatPeriod(report.period) }}
                            </div>
                            <div class="meta-line">
                                <i class="fas fa-clock meta-icon"></i>
                                수정시간 : {{ report.modDate || "-" }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 플로팅: 보고서 생성 트리거 -->
        <button type="button" class="fab-create" title="보고서 생성" @click="openCreateModal">
            <i class="fas fa-plus"></i>
        </button>

        <!-- ============================
             보고서 생성 모달
        ============================= -->
        <b-modal
            ref="createModal"
            title="보고서 생성"
            size="md"
            @hidden="resetCreateForm"
            :no-close-on-backdrop="creating"
        >
            <div v-if="creating" class="creating-overlay">
                <div class="creating-inner">
                    <i class="fas fa-cog fa-spin creating-icon"></i>
                    <p class="creating-text">보고서를 생성하고 있습니다...</p>
                    <p class="creating-sub">PDF 생성 및 증빙 패키지 준비 중입니다. 잠시 기다려주세요.</p>
                </div>
            </div>

            <form v-else @submit.prevent>
                <!-- 워크플로우 선택 -->
                <div class="form-group">
                    <label class="form-label"> 워크플로우 <span class="required">*</span> </label>
                    <div v-if="loadingWorkflows" class="form-loading">
                        <i class="fas fa-spinner fa-spin"></i> 워크플로우 목록 로드 중...
                    </div>
                    <b-form-select
                        v-else
                        v-model="createForm.workflowIdx"
                        :options="workflowOptions"
                        class="form-control"
                        :disabled="!!presetWorkflowIdx"
                        required
                    >
                        <template #first>
                            <b-form-select-option :value="null" disabled>
                                워크플로우를 선택하세요
                            </b-form-select-option>
                        </template>
                    </b-form-select>
                    <small class="form-hint">검증완료(VERIFIED) / 확정완료(CONFIRMED) 상태만 선택 가능합니다.</small>
                </div>

                <!-- 보고서 유형 -->
                <div class="form-group">
                    <label class="form-label">보고서 유형</label>
                    <b-form-select v-model="createForm.reportType" :options="reportTypeOptions" class="form-control" />
                </div>

                <!-- 보고서 옵션 -->
                <div class="form-group">
                    <label class="form-label">보고서 포함 내용</label>
                    <div class="options-grid">
                        <label class="option-item" :class="{ 'is-checked': createForm.reportOptions.includeEvidence }">
                            <div class="option-content">
                                <span class="option-text">
                                    <i class="fas fa-paperclip"></i>
                                    검증/증빙 요약
                                </span>
                                <small class="option-desc"
                                    >증빙 커버리지 + 파일 목록 + 누락 항목 / PDF와 함께 증빙 ZIP 패키지 자동 생성</small
                                >
                            </div>
                            <div class="option-toggle">
                                <input
                                    type="checkbox"
                                    v-model="createForm.reportOptions.includeEvidence"
                                    class="toggle-input"
                                />
                                <span class="toggle-track"><span class="toggle-thumb"></span></span>
                            </div>
                        </label>
                        <label
                            class="option-item"
                            :class="{ 'is-checked': createForm.reportOptions.includeTargetStatus }"
                        >
                            <div class="option-content">
                                <span class="option-text">
                                    <i class="fas fa-bullseye"></i>
                                    감축 목표 이행 현황
                                </span>
                                <small class="option-desc"
                                    >목표별 프로그레스 바, 누적 꺾은선 차트, 월별 달성 트렌드</small
                                >
                            </div>
                            <div class="option-toggle">
                                <input
                                    type="checkbox"
                                    v-model="createForm.reportOptions.includeTargetStatus"
                                    class="toggle-input"
                                />
                                <span class="toggle-track"><span class="toggle-thumb"></span></span>
                            </div>
                        </label>
                        <label
                            class="option-item"
                            :class="{ 'is-checked': createForm.reportOptions.includeAuditHistory }"
                        >
                            <div class="option-content">
                                <span class="option-text">
                                    <i class="fas fa-history"></i>
                                    산정 관리 이력
                                </span>
                                <small class="option-desc">워크플로우 상태 전이 이력, 증빙 검증 예외 처리 이력</small>
                            </div>
                            <div class="option-toggle">
                                <input
                                    type="checkbox"
                                    v-model="createForm.reportOptions.includeAuditHistory"
                                    class="toggle-input"
                                />
                                <span class="toggle-track"><span class="toggle-thumb"></span></span>
                            </div>
                        </label>
                    </div>
                </div>
            </form>

            <template #modal-footer>
                <b-button variant="secondary" @click="$refs.createModal.hide()" :disabled="creating"> 취소 </b-button>
                <b-button variant="primary" @click="handleCreateReport" :disabled="creating || !createForm.workflowIdx">
                    <i class="fas fa-cog" v-if="!creating"></i>
                    <i class="fas fa-spinner fa-spin" v-else></i>
                    {{ creating ? "생성 중..." : "보고서 생성" }}
                </b-button>
            </template>
        </b-modal>

        <!-- ============================
             미리보기 모달
        ============================= -->
        <b-modal
            ref="previewModal"
            :title="previewReport ? previewReport.title || '보고서 미리보기' : '보고서 미리보기'"
            size="xl"
            hide-footer
            body-class="preview-modal-body"
        >
            <div v-if="loadingPreview" class="preview-loading">
                <i class="fas fa-spinner fa-spin preview-loading-icon"></i>
                <p>미리보기 이미지를 불러오는 중...</p>
            </div>
            <div v-else-if="previewImageSrc" class="preview-image-wrap">
                <img :src="previewImageSrc" alt="보고서 미리보기" class="preview-image" />
            </div>
            <div v-else class="preview-empty">
                <i class="fas fa-image preview-empty-icon"></i>
                <p>미리보기 이미지를 불러올 수 없습니다.</p>
            </div>
        </b-modal>

        <!-- ============================
             보고서 수정 모달
        ============================= -->
        <b-modal
            ref="editModal"
            title="보고서 수정"
            size="sm"
            @hidden="resetEditForm"
            ok-title="저장"
            cancel-title="취소"
            :ok-disabled="editSaving"
            @ok.prevent="handleSaveEdit"
        >
            <form @submit.prevent="handleSaveEdit">
                <!-- 제목 -->
                <div class="form-group">
                    <label class="form-label">제목</label>
                    <input
                        v-model="editForm.title"
                        type="text"
                        class="form-control"
                        maxlength="200"
                        placeholder="보고서 제목을 입력하세요"
                        :disabled="editSaving"
                    />
                    <small class="form-hint">최대 200자</small>
                </div>

                <!-- 보고서 상태 -->
                <div class="form-group">
                    <label class="form-label">보고서 상태</label>
                    <b-form-select
                        v-model="editForm.reportStat"
                        :options="reportStatOptions"
                        class="form-control"
                        :disabled="editSaving"
                    />
                    <small class="form-hint">
                        <i class="fas fa-info-circle"></i>
                        보고서 내용 변경은 재생성으로 대체됩니다.
                    </small>
                </div>
            </form>

            <template #modal-footer="{ cancel }">
                <b-button variant="secondary" @click="cancel()" :disabled="editSaving">취소</b-button>
                <b-button variant="primary" @click="handleSaveEdit" :disabled="editSaving">
                    <i class="fas fa-spinner fa-spin" v-if="editSaving"></i>
                    <i class="fas fa-save" v-else></i>
                    {{ editSaving ? "저장 중..." : "저장" }}
                </b-button>
            </template>
        </b-modal>

        <!-- 보고서 증빙 모달 -->
        <EvidenceModal
            :visible="showReportEvidenceModal"
            evidence-category="REPORT"
            :entity-idx="reportEvidenceTarget?.reportIdx ?? null"
            :modal-title-override="reportEvidenceTarget ? `증빙자료 - ${reportEvidenceTarget.title || '보고서'}` : ''"
            @close="showReportEvidenceModal = false"
            @updated="handleReportEvidenceUpdated"
        />
    </div>
</template>

<script>
import carbonApi from "@/submodules/carbon-plugin/api";
import CarbonCodes from "@/submodules/carbon-plugin/codes/carbonCodes";
import { CustomDropdown } from "../common";
import EvidenceModal from "../activity/modals/EvidenceModal.vue";

export default {
    name: "ReportBuilder",
    components: { CustomDropdown, EvidenceModal },
    props: {
        loginUser: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        filterWorkflowIdx: {
            type: [Number, String, null],
            default: null,
        },
        filterReportStat: {
            type: [String, null],
            default: null,
        },
        presetWorkflowIdx: {
            type: Number,
            default: null,
        },
        presetWorkflowName: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            // 목록
            reports: [],
            loading: false,

            // 보고서 생성 모달
            creating: false,
            loadingWorkflows: false,
            workflows: [],
            createForm: {
                workflowIdx: null,
                reportType: "GHG_INVENTORY",
                format: "pdf",
                language: "ko",
                reportOptions: {
                    includeEvidence: true,
                    includeTargetStatus: false,
                    includeAuditHistory: true,
                },
            },

            // 보고서 수정 모달 (제목 + 상태)
            editingReport: null,
            editForm: { title: "", reportStat: "" },
            editSaving: false,

            // 다운로드 상태
            downloadingIdx: null,

            // 미리보기 모달
            previewReport: null,
            previewImageSrc: null,
            loadingPreview: false,

            // 삭제 모달
            deletingReport: null,

            // 보고서 증빙 모달
            showReportEvidenceModal: false,
            reportEvidenceTarget: null,
        };
    },
    computed: {
        filteredReports() {
            let list = this.reports || [];
            if (this.filterWorkflowIdx != null && this.filterWorkflowIdx !== "") {
                list = list.filter((r) => r.workflowIdx == this.filterWorkflowIdx);
            }
            if (this.filterReportStat) {
                list = list.filter((r) => r.reportStat === this.filterReportStat);
            }
            return list;
        },
        workflowOptions() {
            const base = this.workflows.map((wf) => ({
                value: wf.workflowIdx,
                text: `[${this.getPeriodStatText(wf.periodStat)}] ${wf.workflowName || wf.workflowIdx} (${this.formatPeriod(wf.fromMonth)} ~ ${this.formatPeriod(wf.toMonth)})`,
            }));
            if (this.presetWorkflowIdx != null && !base.some((o) => o.value === this.presetWorkflowIdx)) {
                return [
                    {
                        value: this.presetWorkflowIdx,
                        text: this.presetWorkflowName || `워크플로우 #${this.presetWorkflowIdx}`,
                    },
                    ...base,
                ];
            }
            return base;
        },
        reportTypeOptions() {
            return CarbonCodes.reportContentType.getList();
        },
        reportStatOptions() {
            return CarbonCodes.reportStat.getList();
        },
    },
    methods: {
        openReportEvidenceModal(report) {
            if (!report?.reportIdx) return;
            this.reportEvidenceTarget = { reportIdx: report.reportIdx, title: report.title };
            this.showReportEvidenceModal = true;
        },
        handleReportEvidenceUpdated() {
            this.loadReports();
        },
        getReportMenuItems(report) {
            const pdfLoading = this.downloadingIdx === `pdf_${report.reportIdx}`;
            const zipLoading = this.downloadingIdx === `zip_${report.reportIdx}`;
            return [
                {
                    icon: "fa-file-pdf",
                    label: "PDF 다운로드",
                    disabled: pdfLoading,
                    onClick: () => this.downloadPdf(report),
                },
                ...(report.reportOptions && report.reportOptions.includeEvidence
                    ? [
                          {
                              icon: "fa-file-archive",
                              label: "증빙 ZIP 다운로드",
                              disabled: zipLoading,
                              onClick: () => this.downloadZip(report),
                          },
                      ]
                    : []),
                {
                    icon: "fa-paperclip",
                    label: "증빙 관리",
                    onClick: () => this.openReportEvidenceModal(report),
                },
                {
                    icon: "fa-edit",
                    label: "수정",
                    onClick: () => this.openEditModal(report),
                },
                {
                    icon: "fa-trash-alt",
                    label: "삭제",
                    variant: "danger",
                    onClick: () => this.confirmDelete(report),
                },
            ];
        },

        // ─── 데이터 로드 ───

        async loadReports() {
            this.loading = true;
            try {
                const res = await carbonApi.report.getReports({});
                const data = res.data?.data || res.data;
                const list = data?.reports || [];
                list.forEach((r) => {
                    r.thumbnailDataUrl = null;
                    r.thumbnailLoading = true;
                });
                this.reports = list;
                this.loadReportThumbnails();
            } catch (err) {
                console.error("[ReportBuilder] 보고서 목록 조회 실패:", err);
                this.showToast("오류", err.error.message, "danger");
            } finally {
                this.loading = false;
            }
        },

        // Blob → Base64 (CSP 대응, FacilityManagementInfo와 동일 방식)
        blobToBase64(blob) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        },
        async createImageUrl(blob) {
            try {
                return await this.blobToBase64(blob);
            } catch (error) {
                console.error("[ReportBuilder] Base64 변환 실패:", error);
                return URL.createObjectURL(blob);
            }
        },
        async loadReportThumbnails() {
            for (const report of this.reports) {
                if (!report.reportIdx) continue;
                try {
                    const res = await carbonApi.report.getThumbnail(report.reportIdx);
                    const blob = res?.data;
                    if (blob) {
                        const url = await this.createImageUrl(blob);
                        this.$set(report, "thumbnailDataUrl", url);
                    }
                } catch (err) {
                    console.warn(`[ReportBuilder] 썸네일 로드 실패 reportIdx=${report.reportIdx}:`, err);
                    this.$bvToast.toast(err.error.message, {
                        title: "오류",
                        variant: "danger",
                        autoHideDelay: 3000,
                    });
                } finally {
                    this.$set(report, "thumbnailLoading", false);
                }
            }
        },

        async loadWorkflows() {
            this.loadingWorkflows = true;
            try {
                const res = await carbonApi.workflow.getInventoryWorkflows({});
                const data = res.data?.data || res.data;
                const allWorkflows = data?.workflows || data || [];
                // 산정완료(CALCULATED) / 검증완료(VERIFIED) / 확정완료(CONFIRMED) 만 노출
                const allowedStats = ["VERIFIED", "CONFIRMED"];
                this.workflows = allWorkflows.filter((wf) => allowedStats.includes(wf.periodStat));
            } catch (err) {
                console.error("[ReportBuilder] 워크플로우 목록 조회 실패:", err);
                this.showToast("오류", err.error.message, "danger");
            } finally {
                this.loadingWorkflows = false;
            }
        },

        // ─── 보고서 생성 ───

        openCreateModal() {
            this.resetCreateForm();
            if (this.presetWorkflowIdx != null) {
                this.createForm.workflowIdx = this.presetWorkflowIdx;
            }
            this.$refs.createModal.show();
            this.loadWorkflows();
        },

        resetCreateForm() {
            this.createForm = {
                workflowIdx: null,
                reportType: "GHG_INVENTORY",
                format: "pdf",
                language: "ko",
                reportOptions: {
                    includeEvidence: true,
                    includeTargetStatus: false,
                    includeAuditHistory: true,
                },
            };
            this.workflows = [];
        },

        async handleCreateReport() {
            if (!this.createForm.workflowIdx) {
                this.showToast("경고", "워크플로우를 선택하세요.", "warning");
                return;
            }
            this.creating = true;
            try {
                const res = await carbonApi.report.generateReport({
                    workflowIdx: this.createForm.workflowIdx,
                    reportType: this.createForm.reportType,
                    format: this.createForm.format,
                    language: this.createForm.language,
                    reportOptions: { ...this.createForm.reportOptions },
                });
                const report = res.data?.data || res.data;
                this.showToast("완료", report.message, "success");
                this.$refs.createModal.hide();
                await this.loadReports();
            } catch (err) {
                console.error("[ReportBuilder] 보고서 생성 실패:", err);
                this.showToast("오류", err.error.message, "danger");
            } finally {
                this.creating = false;
            }
        },

        // ─── 보고서 수정 모달 (제목 + 상태) ───

        openEditModal(report) {
            this.editingReport = report;
            this.editForm = {
                title: report.title || "",
                reportStat: report.reportStat || "DRAFT",
            };
            this.$refs.editModal.show();
        },

        resetEditForm() {
            this.editingReport = null;
            this.editForm = { title: "", reportStat: "" };
            this.editSaving = false;
        },

        async handleSaveEdit() {
            if (!this.editingReport) return;

            const payload = {};
            const trimmedTitle = this.editForm.title.trim();

            if (trimmedTitle !== (this.editingReport.title || "").trim()) {
                payload.title = trimmedTitle;
            }
            if (this.editForm.reportStat !== this.editingReport.reportStat) {
                payload.reportStat = this.editForm.reportStat;
            }

            // 변경 사항 없으면 그냥 닫기
            if (Object.keys(payload).length === 0) {
                this.$refs.editModal.hide();
                return;
            }

            this.editSaving = true;
            try {
                let res = await carbonApi.report.updateReport(this.editingReport.reportIdx, payload);
                // 목록 반영 (서버 재조회 없이 로컬 업데이트)
                if (payload.title !== undefined) this.editingReport.title = payload.title;
                if (payload.reportStat !== undefined) this.editingReport.reportStat = payload.reportStat;

                this.showToast("완료", res.data.message, "success");
                this.$refs.editModal.hide();
            } catch (err) {
                console.error("[ReportBuilder] 보고서 수정 실패:", err);
                this.showToast("오류", err.error.message, "danger");
            } finally {
                this.editSaving = false;
            }
        },

        // ─── 다운로드 ───

        async downloadPdf(report) {
            const key = `pdf_${report.reportIdx}`;
            this.downloadingIdx = key;
            try {
                let res = await carbonApi.report.downloadReport(report.reportIdx);
                this.showToast("완료", "PDF 다운로드가 시작되었습니다.", "success");
                this.triggerDownload(res.data, `${report.title || "보고서"}.pdf`, "application/pdf");
            } catch (err) {
                console.error("[ReportBuilder] PDF 다운로드 실패:", err);
                this.showToast("오류", err.error.message, "danger");
            } finally {
                this.downloadingIdx = null;
            }
        },

        async downloadZip(report) {
            const key = `zip_${report.reportIdx}`;
            this.downloadingIdx = key;
            try {
                let res = await carbonApi.report.downloadPackage(report.reportIdx);
                this.triggerDownload(res.data, `${report.title || "보고서"}-증빙패키지.zip`, "application/zip");
            } catch (err) {
                console.error("[ReportBuilder] ZIP 다운로드 실패:", err);
                this.showToast("오류", err.error.message, "danger");
            } finally {
                this.downloadingIdx = null;
            }
        },

        triggerDownload(blob, fileName, mimeType) {
            const url = URL.createObjectURL(new Blob([blob], { type: mimeType }));
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        },

        // ─── 미리보기 모달 ───

        async openPreviewModal(report) {
            if (!report.previewUrl) return;
            this.previewReport = report;
            this.previewImageSrc = null;
            this.loadingPreview = true;
            this.$refs.previewModal.show();
            try {
                let res = await carbonApi.report.getPreview(report.reportIdx);
                const blob = res?.data;
                if (blob) {
                    // Blob → Base64 (CSP 대응, 썸네일과 동일 방식)
                    this.previewImageSrc = await this.createImageUrl(blob);
                }
            } catch (err) {
                console.error("[ReportBuilder] 미리보기 조회 실패:", err);
                this.showToast("오류", err.error.message, "danger");
                this.previewImageSrc = null;
            } finally {
                this.loadingPreview = false;
            }
        },

        // ─── 삭제 ───

        async confirmDelete(report) {
            this.deletingReport = report;
            let value = await this.alertConfirmWarning(`${this.deletingReport.title || "보고서"}를 삭제하시겠습니까?`);
            if (value.value) {
                this.handleDeleteReport();
            }
        },

        async handleDeleteReport() {
            if (!this.deletingReport) return;
            try {
                let res = await carbonApi.report.deleteReport(this.deletingReport.reportIdx);
                this.showToast("완료", res.data.message, "success");
                this.reports = this.reports.filter((r) => r.reportIdx !== this.deletingReport.reportIdx);
            } catch (err) {
                console.error("[ReportBuilder] 보고서 삭제 실패:", err);
                this.showToast("오류", err.error.message, "danger");
            } finally {
                this.deletingReport = null;
            }
        },

        // ─── 유틸 / 포맷 ───

        handleThumbnailError(event, report) {
            this.$set(report, "thumbnailDataUrl", null);
            if (event && event.target) event.target.style.display = "none";
        },

        formatPeriod(period) {
            if (!period) return "-";
            const s = String(period);
            if (s.length === 6) return `${s.substring(0, 4)}년 ${s.substring(4, 6)}월`;
            if (s.length === 4) return `${s}년`;
            return period;
        },

        getStatClass(stat) {
            if (stat === "PUBLISHED") return "stat-published";
            if (stat === "DRAFT") return "stat-draft";
            return "stat-inactive";
        },

        getStatText(stat) {
            return CarbonCodes.reportStat.getLabel(stat) || stat;
        },

        getPeriodStatText(stat) {
            return CarbonCodes.periodStat.getLabel(stat) || stat;
        },

        showToast(title, message, variant = "info") {
            if (this.$bvToast) {
                this.$bvToast.toast(message, {
                    variant,
                    title: title || undefined,
                    autoHideDelay: 3000,
                });
            }
        },
    },
    mounted() {
        this.loadReports();
    },
};
</script>

<style scoped>
/* ─── 기본 레이아웃 ─── */
.report-builder {
    color: #1e293b;
    padding: 0;
    height: 100%;
    position: relative;
}

/* ─── 플로팅 생성 버튼 ─── */
.fab-create {
    position: absolute;
    right: 26px;
    bottom: 12px;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #0f766e;
    color: #fff;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    box-shadow: 0 4px 12px rgba(15, 118, 110, 0.4);
    transition:
        background 0.2s,
        box-shadow 0.2s,
        transform 0.2s;
    z-index: 10;
}

.fab-create:hover {
    background: #0d9488;
    box-shadow: 0 6px 16px rgba(15, 118, 110, 0.45);
    transform: scale(1.05);
}

/* 빈 상태용 버튼 (같은 스타일 유지) */
.action-btn {
    padding: 8px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.btn-create {
    background: #0f766e;
    color: #fff;
}

.btn-create:hover:not(:disabled) {
    background: #0d9488;
}

.action-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* ─── 보고서 목록 섹션 ─── */
.report-list-section {
    max-height: 620px;
    overflow-y: auto;
    padding-right: 8px;
}

/* 상태 컨테이너 (로딩/빈 상태) */
.state-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #94a3b8;
    text-align: center;
    background: white;
    border-radius: 12px;
    border: 2px dashed #e2e8f0;
}

.state-icon {
    font-size: 40px;
    margin-bottom: 12px;
}

.empty-icon {
    color: #cbd5e1;
}

.state-text {
    font-size: 16px;
    font-weight: 600;
    color: #64748b;
    margin: 0 0 4px;
}

.state-sub {
    font-size: 13px;
    color: #94a3b8;
    margin: 0;
}

/* ─── 보고서 카드 그리드 ─── */
.report-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 14px;
}

/* ─── 보고서 카드 ─── */
.report-card {
    background: white;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: all 0.2s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

/* .card-published {
    border-color: #d8f3dc;
    border-left: 4px solid #2d8659;
}

.card-draft {
    border-left: 4px solid #94a3b8;
} */

/* 썸네일 */
.card-thumbnail {
    position: relative;
    height: 160px;
    background: #f8fafc;
    cursor: pointer;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid #e2e8f0;
}

.thumbnail-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.2s ease;
}

.card-thumbnail:hover .thumbnail-img {
    transform: scale(1.03);
}

.thumbnail-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: #f1f5f9;
}

.thumbnail-placeholder.thumbnail-loading .placeholder-icon {
    color: #0f766e;
}

.placeholder-icon {
    font-size: 48px;
    color: #cbd5e1;
}

.thumbnail-overlay {
    position: absolute;
    inset: 0;
    background: rgba(18, 70, 45, 0.65);
    color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.thumbnail-overlay i {
    font-size: 22px;
}

.card-thumbnail:hover .thumbnail-overlay {
    opacity: 1;
}

/* 카드 본문 */
.card-body {
    padding: 14px 16px;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.card-title-row {
    display: flex;
    align-items: center;
    gap: 6px;
}

.card-title {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.report-card-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    min-height: 36px;
    flex-shrink: 0;
    padding: 0;
    border-radius: 8px;
    color: #94a3b8;
    cursor: pointer;
    transition:
        background-color 0.2s,
        color 0.2s;
}

.report-card-trigger:hover {
    background-color: #f1f5f9;
    color: #475569;
}

.report-card:hover .report-card-trigger {
    color: #64748b;
}

.report-card-trigger-icon {
    font-size: 13px;
    color: inherit;
}

/* 메타 */
.card-meta {
}

.meta-line {
    font-size: 12px;
    color: #64748b;
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
}

.meta-line .meta-icon {
    font-size: 10px;
    color: #94a3b8;
    flex-shrink: 0;
}

.meta-line + .meta-line {
    margin-top: 2px;
}

/* 배지 */
.card-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
}

.badge-stat {
    font-size: 10px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
    letter-spacing: 0.3px;
}

.stat-published {
    background: #d8f3dc;
    color: #0f4a35;
}

.stat-draft {
    background: #f1f5f9;
    color: #475569;
}

.stat-inactive {
    background: #fee2e2;
    color: #991b1b;
}

.badge-option,
.badge-evidence {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 10px;
    background: #eff6ff;
    color: #1d4ed8;
    display: flex;
    align-items: center;
    gap: 3px;
}

.badge-evidence {
    background: #fef3c7;
    color: #92400e;
}

/* ─── 생성 모달 ─── */
.creating-overlay {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
}

.creating-inner {
    text-align: center;
}

.creating-icon {
    font-size: 40px;
    color: #12462d;
    margin-bottom: 16px;
}

.creating-text {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 6px;
}

.creating-sub {
    font-size: 13px;
    color: #64748b;
    margin: 0;
}

.form-label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 5px;
    display: block;
}

.required {
    color: #e53e3e;
}

.form-hint {
    font-size: 11px;
    color: #64748b;
    margin-top: 4px;
    display: block;
}

.form-loading {
    padding: 10px;
    font-size: 13px;
    color: #64748b;
}

.form-row {
    display: flex;
    gap: 12px;
}

.form-row .form-group {
    flex: 1;
}

.options-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* 옵션 카드: 좌측 텍스트 + 우측 토글 */
.option-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    cursor: pointer;
    padding: 12px 14px;
    background: white;
    border-radius: 8px;
    border: 1.5px solid #e2e8f0;
    transition:
        border-color 0.15s,
        background 0.15s;
    margin: 0;
}

.option-item:hover {
    border-color: #52b788;
    background: #f8fffe;
}

.option-item.is-checked {
    border-color: #52b788;
    background: #f0faf4;
}

/* 좌측: 텍스트 영역 */
.option-content {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;
    min-width: 0;
}

.option-text {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.option-text i {
    color: #12462d;
    font-size: 12px;
    flex-shrink: 0;
}

.option-desc {
    font-size: 11px;
    color: #64748b;
    margin: 0;
    line-height: 1.5;
}

/* 우측: 토글 스위치 */
.option-toggle {
    position: relative;
    flex-shrink: 0;
}

.toggle-input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
}

.toggle-track {
    display: block;
    width: 40px;
    height: 22px;
    background: #d1d5db;
    border-radius: 11px;
    position: relative;
    transition: background 0.2s ease;
    cursor: pointer;
}

.toggle-thumb {
    display: block;
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 50%;
    position: absolute;
    top: 3px;
    left: 3px;
    transition: transform 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* 체크 상태: 트랙 색상 + 썸 이동 */
.option-item.is-checked .toggle-track {
    background: #12462d;
}

.option-item.is-checked .toggle-thumb {
    transform: translateX(18px);
}

/* ─── 미리보기 모달 ─── */
.preview-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #64748b;
}

.preview-loading-icon {
    font-size: 36px;
    margin-bottom: 12px;
    color: #12462d;
}

.preview-image-wrap {
    overflow-y: auto;
    display: flex;
    justify-content: center;
    background: #f8fafc;
    padding: 16px;
}

.preview-image {
    max-width: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
}

.preview-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    color: #94a3b8;
}

.preview-empty-icon {
    font-size: 40px;
    margin-bottom: 12px;
}

/* ─── 삭제 확인 모달 ─── */
.delete-confirm {
    text-align: center;
    padding: 10px 0;
}

.delete-warn-icon {
    font-size: 36px;
    color: #f59e0b;
    margin-bottom: 12px;
}

.delete-warn-text {
    font-size: 12px;
    color: #64748b;
    margin-top: 8px;
}

/* ─── 반응형 ─── */
@media (max-width: 768px) {
    .report-grid {
        grid-template-columns: 1fr;
    }

    .form-row {
        flex-direction: column;
        gap: 0;
    }
}

.mt-3 {
    margin-top: 12px;
}
</style>
